import ExcelJS from 'exceljs';

/**
 * Create and download an Excel workbook.
 * Thin wrapper around ExcelJS matching the patterns used throughout the app.
 */

export interface ExcelSheet {
  name: string;
  data: (string | number | null | undefined)[][];
  headers?: string[];
}

/**
 * Create a workbook from sheets and trigger a file download.
 */
export async function writeExcelFile(sheets: ExcelSheet[], filename: string): Promise<void> {
  const workbook = new ExcelJS.Workbook();

  for (const sheet of sheets) {
    const worksheet = workbook.addWorksheet(sheet.name);
    if (sheet.headers) {
      worksheet.addRow(sheet.headers);
    }
    for (const row of sheet.data) {
      worksheet.addRow(row);
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Convert an array of objects to a sheet (headers from keys, rows from values).
 * Equivalent to XLSX.utils.json_to_sheet.
 */
export function jsonToSheet(data: Record<string, unknown>[]): ExcelSheet {
  if (data.length === 0) return { name: '', data: [], headers: [] };
  const headers = Object.keys(data[0]);
  const rows = data.map(obj => headers.map(h => {
    const val = obj[h];
    return val === null || val === undefined ? '' : val;
  })) as (string | number)[][];
  return { name: '', data: rows, headers };
}

/**
 * Convert an array of objects to CSV string.
 * Equivalent to XLSX.utils.json_to_sheet + sheet_to_csv.
 */
export function jsonToCSV(data: Record<string, unknown>[]): string {
  if (data.length === 0) return '';
  const headers = Object.keys(data[0]);
  const rows = data.map(obj =>
    headers.map(h => {
      const val = obj[h];
      const str = val === null || val === undefined ? '' : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    }).join(',')
  );
  return [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
}
